import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtilsService } from '../../commons/http-uilis.service';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';
import { Paging } from '../../../model/paging/paging';
import { environment } from '../../../environment/environment';
import { UserList, UserListData, UserListDataArray } from '../../../model/user/users-list';
import { EmailLicense } from '../../../model/email-license/email-license';
import { keycloakLogout } from '../../../auth/keycloak.config';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _url: string = `${environment.baseAPIURL}/settings`;

  constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) {}

  GETPaginatedUsers(paging: Paging): Observable<UserList> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.get(`${this._url}/users${this.buildQueryString(paging)}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: UserListDataArray = jsonConvert.deserializeObject(res, UserListDataArray);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  GETUserByID(userId: string): Observable<UserList> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.get(`${this._url}/users/${userId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: UserListData = jsonConvert.deserializeObject(res, UserListData);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  POSTUser(userData: UserList): Observable<UserList> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.post(`${this._url}/users`, userData.toCreatePayload(), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  PUTUser(userId: string, userData: UserList): Observable<UserList> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.put(`${this._url}/users/${userId}`, userData.toUpdatePayload(), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  DELETEUser(userId: string): Observable<boolean> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.delete(`${this._url}/users/${userId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  PUTUserStatus(userId: string, enabled: boolean) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.put(`${this._url}/users/${userId}/enable`, { enabled }, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  POSTSendLicenseToUser(sendEmailBody: EmailLicense, userId: string) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.post(`${this._url}/users/${userId}/send-license`, sendEmailBody, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  private buildQueryString(paging: Paging): string {
    const params = new URLSearchParams({
      offset: String((paging.pageIndex - 1) * paging.pageSize),
      limit: String(paging.pageSize)
    });
    const sort = paging.sort.find(item => item.value !== null);
    if (sort?.key) {
      params.set('sortBy', sort.key);
    }
    if (sort?.value === 'ascend') {
      params.set('sortOrder', 'asc');
    } else if (sort?.value === 'descend') {
      params.set('sortOrder', 'desc');
    }
    return `?${params.toString()}`;
  }
}
